/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import moment from 'moment';
import cx from 'classnames';
import { isNil } from 'lodash';

import TextFieldStateless from './TextFieldStateless';
import { VisualOnlyLabel } from '../Label';
import announce from '../../../helpers/announce';

export default class BirthdateField extends React.PureComponent {
    static propTypes = {
        /** Label contents, usually text */
        label: PropTypes.node,

        /** html id required to connect the input with the label */
        id: PropTypes.string.isRequired,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** The input's value. Use only when the input is controlled */
        value: momentPropTypes.momentObj,

        /** Initial value the field should have on mount */
        defaultValue: momentPropTypes.momentObj,

        /** Disables the field */
        isDisabled: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Additional className added to outer container for custom styling */
        className: PropTypes.string,

        /** Function fired when the field changes */
        onChange: PropTypes.func,

        /** Performs the validation as soon as the component mounts */
        shouldValidateOnMount: PropTypes.bool,

        /** Enables validation as you type */
        shouldValidateOnUpdate: PropTypes.bool,

        /** Callback invoked when isInvalid/errorText changes state. Returns an
        object with the id, isValid, and errorText */
        handleNewErrorState: PropTypes.func,

        /** Manually marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Manually set contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Validation doesn't fail for future dates */
        allowUnborn: PropTypes.bool,

        /** Validation doesn't fail for dates more than 120 years in the past */
        allowDeceased: PropTypes.bool
    };

    static defaultProps = {
        label: 'Birthdate',
        isLabelVisible: true,
        value: undefined,
        defaultValue: undefined,
        isDisabled: false,
        isRequired: false,
        className: undefined,
        onChange: undefined,
        shouldValidateOnMount: false,
        shouldValidateOnUpdate: false,
        handleNewErrorState: undefined,
        allowUnborn: false,
        allowDeceased: false
    };

    constructor(props) {
        super(props);

        const hasErrorText = typeof props.errorText === 'string'
            && props.errorText.length > 0;

        const initialValue = props.value || props.defaultValue || null;
        let month = '';
        let day = '';
        let year = '';

        if (initialValue) {
            month = initialValue.format('MM');
            day = initialValue.format('DD');
            year = initialValue.format('YYYY');
        }

        this.state = {
            month,
            day,
            year,
            isPristine: !initialValue,
            isInvalid: props.isInvalid || hasErrorText || false,
            errorText: props.errorText || null
        };

        this.errorState = {
            isInvalid: props.isInvalid,
            errorText: props.errorText
        };

        this.wrapperRef = React.createRef();
        this.monthRef = React.createRef();
        this.dayRef = React.createRef();
        this.yearRef = React.createRef();
    }

    componentDidMount() {
        const { shouldValidateOnMount } = this.props;

        if (shouldValidateOnMount) {
            this.validate();
        }
    }

    componentDidUpdate(prevProps) {
        const { value, shouldValidateOnUpdate } = this.props;

        if (value !== prevProps.value) {
            let month = '';
            let day = '';
            let year = '';

            if (value) {
                month = value.format('MM');
                day = value.format('DD');
                year = value.format('YYYY');
            }

            this.setState({
                month,
                day,
                year
            });

            if (shouldValidateOnUpdate) {
                this.validate();
            }
        }
    }

    getValue = () => {
        const month = this.monthRef.current.value;
        const day = this.dayRef.current.value;
        const year = this.yearRef.current.value;

        let dateMoment = null;

        if (month.length && day.length && year.length) {
            const dateString = `${month}-${day}-${year}`;
            dateMoment = moment(dateString, 'MM-DD-YYYY');
        }

        return dateMoment;
    }

    isControlled() {
        const { onChange } = this.props;
        if (!isNil(onChange)) {
            return true;
        }
        return false;
    }

    handleMonthChange = (event) => {
        event.preventDefault();

        const value = event.target.value;

        // The input string should only contain number characters.
        const isNumber = /^\d+$/.test(value);

        if (isNumber) {
            // If the input string only contains numbers, we convert it to an actual number
            const valueAsNum = parseInt(value, 10);

            // If that number is potentially a valid month number (we can't
            // check for 0 because the user might be typing "02")
            if (valueAsNum <= 12) {
                // We add the value to the state ...
                this.setState({
                    month: value,
                    isPristine: false
                });

                // ... And if the input string is 2 chars long (i.e. '02' or '12'),
                // Then we call changeHandler which potentially sends the input
                // values back to the user's onChange prop
                if (value.length === 2) {
                    this.changeHandler();
                }
            }
            // If the user entered a number higher than 12
            else {
                announce('Error: Enter a value between 1 and 12.');
            }
        }
        // If the user has backspaced over the input, then we set the state to
        // an empty string
        else if (value === '') {
            this.setState({
                month: ''
            });

            this.changeHandler();
        }
        // Else the user entered something other than numbers so we prevent a
        // change to the input and SR users get an error announcement
        else {
            announce('Error: Enter only numbers.');
        }
    }

    handleBlur = () => {
        this.changeHandler();

        const { isPristine } = this.state;

        // Validate if user blurs out of all fields
        // Must set a timeout so the focus can update ... otherwise will always
        // report that the activeElement is the body
        setTimeout(() => {
            if (!isPristine && !this.wrapperRef.current.contains(document.activeElement)) {
                this.validate();
            }
        }, 0);
    }

    handleDayChange = (event) => {
        event.preventDefault();

        const value = event.target.value;

        // The input string should only contain number characters.
        const isNumber = /^\d+$/.test(value);

        if (isNumber) {
            // convert input string to an actual number
            const valueAsNum = parseInt(value, 10);

            // if that number is potentially a valid month ...
            if (valueAsNum <= 31) {
                // ... add it to state ...
                this.setState({
                    day: value,
                    isPristine: false
                });

                // ... and if the input string is 2 chars long, call changeHandler
                // which potentially sends the input values back to the user's
                // onChange prop
                if (value.length === 2) {
                    this.changeHandler();
                }
            }
            // If it's a number but higher than 31, we prevent the input and
            // give an announcement to SR users
            else {
                announce('Error: Enter a value between 1 and 31.');
            }
        }
        else if (value === '') {
            this.setState({
                day: ''
            });

            this.changeHandler();
        }
        else {
            announce('Error: Enter only numbers.');
        }
    }

    handleYearChange = (event) => {
        event.preventDefault();

        const value = event.target.value;

        // The input string should only contain number characters.
        const isNumber = /^\d+$/.test(value);

        if (isNumber) {
            // No preliminary validation checks for the year like other handle methods
            this.setState({ year: value });

            if (value.length === 4) {
                this.changeHandler();
            }
        }
        else if (value === '') {
            this.setState({
                year: ''
            });

            this.changeHandler();
        }
        else {
            announce('Error: Enter only numbers.');
        }
    }

    formatFields = () => {
        const value = this.getValue();

        if (value) {
            const month = value.format('MM');
            const day = value.format('DD');
            const year = value.format('YYYY');

            this.setState({
                month,
                day,
                year
            });
        }
    }

    changeHandler = () => {
        if (!this.isControlled()) {
            this.formatFields();
            return;
        }

        const { onChange } = this.props;
        const { month, day, year } = this.state;

        const internalValue = this.getValue();

        // If all the fields are empty, set the value to null
        if (!month && !day && !year) {
            onChange(null);
        }

        // Else if the internal value is valid ...
        if (internalValue && internalValue._isValid === true) {
            onChange(internalValue);
        }
    }

    clearError = () => {
        this.setState({
            isInvalid: false,
            errorText: undefined
        });
    }

    handleNewErrorState = ({ isInvalid, errorText }) => {
        const { handleNewErrorState, id } = this.props;

        // bail if no handleNewErrorState function exists
        if (!handleNewErrorState) {
            return;
        }

        // compare new state to old state. If different, invoke callback
        if (isInvalid !== this.errorState.isInvalid || errorText !== this.errorState.errorText) {
            handleNewErrorState({
                id,
                isInvalid,
                errorText
            });

            this.errorState = {
                isInvalid,
                errorText
            };
        }
    }

    validate = () => {
        // This method checks that the combination of the input values can be
        // transformed into a valid date. And if isRequired is true, that the
        // inputs are not empty.

        const { isRequired, id, allowUnborn, allowDeceased } = this.props;

        // value is null or a moment object (but not necessarily a valid moment object)
        const value = this.getValue();

        const validationState = {
            isInvalid: false,
            errorText: null,
            id
        };

        // When the field is empty
        if (isNil(value)) {
            if (isRequired) {
                const errorText = typeof this.props.label === 'string'
                    ? `${this.props.label} is required.`
                    : 'This field is required';
                validationState.isInvalid = true;
                validationState.errorText = errorText;
            }
            else {
                // Reset error state
                this.clearError();
            }
        }
        // When there is a value
        else {
            const today = moment();
            const tooOldDate = today.clone().subtract(120, 'years');

            // moment reports that the moment object isn't valid
            if (value._isValid === false) {
                validationState.isInvalid = true;
                validationState.errorText = 'Birthdate is not a valid date.';
            }
            // A valid moment object but it's in the future. Valid only if allowUnborn is true
            else if (!allowUnborn && value.isAfter(today)) {
                validationState.isInvalid = true;
                validationState.errorText = 'Birthdate can not be in the future.';
            }
            // A valid moment object but it's more than 120 years in the past.
            // This person has almost certainly kicked the bucket. Valid only
            // if allowDeceased is true
            else if (!allowDeceased && value.isBefore(tooOldDate)) {
                validationState.isInvalid = true;
                validationState.errorText = 'Birthdate is too far in the past.';
            }
        }

        // Update local component state
        this.setState(validationState);
        this.handleNewErrorState(validationState);

        // Return values to be passed on to onBlur
        return validationState;
    }

    render() {
        const {
            id,
            label,
            isLabelVisible,
            isDisabled,
            isRequired,
            className
        } = this.props;
        const {
            month,
            day,
            year,
            isInvalid,
            errorText
        } = this.state;

        return (
            <div
                ref={this.wrapperRef}
                id={id}
                tabIndex="-1"
                data-component="BirthdateField"
                className={cx('sarsa-birthdate-field', className)}
                role="group"
                aria-label={label}>
                {isLabelVisible && (
                    <VisualOnlyLabel
                        isRequired={isRequired}
                        className="sarsa-birthdate-field--label">
                        {label}
                    </VisualOnlyLabel>
                )}

                <div className="sarsa-birthdate-field--fields-group">
                    <TextFieldStateless
                        innerRef={this.monthRef}
                        className="sarsa-birthdate-field--month"
                        maxLength="2"
                        id={`${id}-month`}
                        label="Month"
                        value={month}
                        onChange={this.handleMonthChange}
                        onBlur={this.handleBlur}
                        isInvalid={isInvalid}
                        isDisabled={isDisabled}
                        ariaDescribedby={errorText && `${id}-error`} />
                    <TextFieldStateless
                        innerRef={this.dayRef}
                        className="sarsa-birthdate-field--day"
                        maxLength="2"
                        id={`${id}-day`}
                        label="Day"
                        value={day}
                        onChange={this.handleDayChange}
                        onBlur={this.handleBlur}
                        isInvalid={isInvalid}
                        isDisabled={isDisabled}
                        ariaDescribedby={errorText && `${id}-error`} />
                    <TextFieldStateless
                        innerRef={this.yearRef}
                        className="sarsa-birthdate-field--year"
                        maxLength="4"
                        id={`${id}-year`}
                        label="Year"
                        value={year}
                        onChange={this.handleYearChange}
                        onBlur={this.handleBlur}
                        isInvalid={isInvalid}
                        isDisabled={isDisabled}
                        ariaDescribedby={errorText && `${id}-error`} />
                </div>
                {errorText && (
                    <div
                        id={`${id}-error`}
                        className="sarsa-field-error-message">
                        <span className="req" aria-hidden="true">*</span>
                        {errorText}
                    </div>
                )}
            </div>
        );
    }
}
